import React, { useState, useEffect } from 'react'
import { User, Phone, Clock, Calendar, IndianRupee, Save, Plus, Trash2, XCircle } from 'lucide-react'

function DriverForm({ onSubmit, onCancel, editingDriver = null, isSubmitting = false }) {
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    advance_amount: '',
    status: 'active'
  })
  const [monthlyEntries, setMonthlyEntries] = useState([
    {
      id: 1,
      work_days: [
        {
          day_id: 1,
          work_date: new Date().toISOString().split('T')[0],
          from_hours: '',
          to_hours: '',
          total_hours: '',
          day_amount: ''
        }
      ],
      salary_amount: 0,
      advance_deduction: ''
    }
  ])
  const [errors, setErrors] = useState({})
  const [monthlyErrors, setMonthlyErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState('')
  const [collapsedWeeks, setCollapsedWeeks] = useState({})

  useEffect(() => {
    if (editingDriver) {
      console.log('Loading editing driver data:', editingDriver)
      
      setFormData({
        name: editingDriver.name || '',
        phone_number: editingDriver.phone_number || '',
        advance_amount: editingDriver.advance_amount !== null && editingDriver.advance_amount !== undefined ? editingDriver.advance_amount : '',
        status: editingDriver.status || 'active'
      })
      
      // If driver has existing weeks_data, populate it from JSONB structure
      if (editingDriver.weeks_data && editingDriver.weeks_data.length > 0) {
        console.log('Found weeks_data:', editingDriver.weeks_data)
        const weeksData = editingDriver.weeks_data.map(week => ({
          id: week.id,
          work_days: week.work_days.map(day => ({
            day_id: day.day_id,
            work_date: day.work_date,
            from_hours: day.from_hours || '',
            to_hours: day.to_hours || '',
            total_hours: day.total_hours || 0,
            day_amount: day.day_amount || ''
          })),
          salary_amount: 0, // Not stored in JSONB, calculated from hours
          advance_deduction: week.advance_deduction || ''
        }))
        console.log('Processed weeks data:', weeksData)
        setMonthlyEntries(weeksData)
        
        // Set all weeks as initially collapsed
        const initialCollapsedState = {}
        weeksData.forEach(week => {
          initialCollapsedState[week.id] = true
        })
        setCollapsedWeeks(initialCollapsedState)
      } else {
        console.log('No weeks_data found, checking old structure...')
        // Fallback to old structure if weeks_data doesn't exist
        if (editingDriver.work_date || editingDriver.salary_amount) {
          console.log('Using old structure with work_date:', editingDriver.work_date)
          setMonthlyEntries([{
            id: 1,
            work_days: [
              {
                day_id: 1,
                work_date: editingDriver.work_date || new Date().toISOString().split('T')[0],
                from_hours: editingDriver.from_hours || '',
                to_hours: editingDriver.to_hours || '',
                total_hours: editingDriver.total_hours || 0,
                day_amount: editingDriver.day_amount || ''
              }
            ],
            salary_amount: editingDriver.salary_amount || 0,
            advance_deduction: editingDriver.advance_deduction || ''
          }])
          
          // Set the single week as initially collapsed
          setCollapsedWeeks({ 1: true })
        } else {
          console.log('No existing data found, using default structure')
        }
      }
    }
  }, [editingDriver])

  // Update work day fields
  const updateWorkDay = (weekId, dayId, field, value) => {
    setMonthlyEntries(prev => prev.map(week => {
      if (week.id === weekId) {
        const updatedWeek = { ...week }
        updatedWeek.work_days = week.work_days.map(day => {
          if (day.day_id === dayId) {
            const updatedDay = { ...day, [field]: value }
            
            // Total hours are now manually entered - no auto-calculation
            
            return updatedDay
          }
          return day
        })
        return updatedWeek
      }
      return week
    }))
  }

  // calculateTotalHours function removed - total hours are now manually entered

  const validateForm = () => {
    const newErrors = {}
    const newMonthlyErrors = {}

    // Validate basic driver information
    if (!formData.name.trim()) {
      newErrors.name = 'Driver name is required'
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required'
    } else if (!/^\d{10}$/.test(formData.phone_number.replace(/\D/g, ''))) {
      newErrors.phone_number = 'Please enter a valid 10-digit phone number'
    }

    if (formData.advance_amount !== '' && parseFloat(formData.advance_amount) < 0) {
      newErrors.advance_amount = 'Advance amount cannot be negative'
    }


          // Validate weekly entries
      monthlyEntries.forEach((week, weekIndex) => {
        const weekErrors = {}
        
        // Salary is now auto-calculated, so no need to validate it
      
      // Validate work days
      week.work_days.forEach((day, dayIndex) => {
        const dayErrors = {}
        
        if (!day.work_date) {
          dayErrors.work_date = 'Work date is required'
        }
        
        // Start and end times are now optional since total hours are manually entered
        // if (!day.from_hours) {
        //   dayErrors.from_hours = 'Start time is required'
        // }
        
        // if (!day.to_hours) {
        //   dayErrors.to_hours = 'End time is required'
        // }
        
        if (!day.total_hours || day.total_hours === '' || parseFloat(day.total_hours) <= 0) {
          dayErrors.total_hours = 'Total hours must be greater than 0'
        }
        
        if (Object.keys(dayErrors).length > 0) {
          weekErrors[day.day_id] = dayErrors
        }
      })
      
      if (Object.keys(weekErrors).length > 0) {
        newMonthlyErrors[week.id] = weekErrors
      }
    })

    setErrors(newErrors)
    setMonthlyErrors(newMonthlyErrors)
    
    return Object.keys(newErrors).length === 0 && Object.keys(newMonthlyErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    try {
      // Prepare weeks_data structure for JSONB storage
      const weeksData = monthlyEntries.map(week => {
        // Filter out days without required data
        const validWorkDays = week.work_days.filter(day => 
          day.work_date && day.total_hours && day.total_hours !== '' && parseFloat(day.total_hours) > 0
        )
        
        if (validWorkDays.length === 0) return null
        
        // Calculate week start and end dates
        const workDates = validWorkDays.map(day => new Date(day.work_date)).sort((a, b) => a - b)
        const weekStartDate = workDates[0].toISOString().split('T')[0]
        const weekEndDate = new Date(workDates[workDates.length - 1])
        weekEndDate.setDate(weekEndDate.getDate() + 6)
        const weekEndDateStr = weekEndDate.toISOString().split('T')[0]
        
        return {
          id: week.id,
          week_start_date: weekStartDate,
          week_end_date: weekEndDateStr,
          advance_deduction: week.advance_deduction === '' ? null : parseFloat(week.advance_deduction) || null,
          work_days: validWorkDays.map(day => ({
            day_id: day.day_id,
            work_date: day.work_date,
            from_hours: day.from_hours || null,
            to_hours: day.to_hours || null,
            total_hours: parseFloat(day.total_hours),
            day_amount: day.day_amount === '' ? null : parseFloat(day.day_amount) || null
          }))
        }
      }).filter(week => week !== null)
      
      if (weeksData.length === 0) {
        alert('Please enter total hours for at least one work day.')
        return
      }
      
      console.log('Submitting driver with weeks_data:', weeksData)
      
      // Create single driver record with all weeks and days
      const { hourly_rate, ...formDataWithoutHourlyRate } = formData
      const submitData = {
        ...formDataWithoutHourlyRate,
        phone_number: formData.phone_number,
        // Convert empty strings to null for numeric fields
        advance_amount: formData.advance_amount === '' ? null : parseFloat(formData.advance_amount) || null,
        weeks_data: weeksData
      }
      
      console.log('Submitting driver data:', submitData)
      
      const result = await onSubmit(submitData)
      
            if (result && result.success) {
        // Show success message - form will be closed by parent component
        setSuccessMessage(`Successfully saved driver with ${weeksData.length} week(s) and ${weeksData.reduce((total, week) => total + week.work_days.length, 0)} day(s)!`)
        
        // Clear success message after 3 seconds (form will be closed)
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        alert(`Error saving driver: ${result?.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error submitting driver form:', error)
      alert('An error occurred while saving. Please try again.')
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters and return only numbers
    return value.replace(/\D/g, '')
  }

  const addMonthlyEntry = () => {
    const newId = Math.max(...monthlyEntries.map(entry => entry.id)) + 1
    
    // Calculate the next week start date
    let newWeekStartDate = new Date().toISOString().split('T')[0]
    if (monthlyEntries.length > 0) {
      const lastWeek = monthlyEntries[monthlyEntries.length - 1]
      if (lastWeek.work_days.length > 0) {
        const lastDate = new Date(Math.max(...lastWeek.work_days.map(day => new Date(day.work_date))))
        lastDate.setDate(lastDate.getDate() + 7) // Start next week
        newWeekStartDate = lastDate.toISOString().split('T')[0]
      }
    }
    
    console.log(`Adding new week ${newId} starting from: ${newWeekStartDate}`)
    
    setMonthlyEntries(prev => [...prev, {
      id: newId,
      work_days: [
        {
          day_id: 1,
          work_date: newWeekStartDate,
          from_hours: '',
          to_hours: '',
          total_hours: '',
          day_amount: ''
        }
      ],
      salary_amount: 0,
      advance_deduction: ''
    }])
    
    // Set the new week as initially collapsed
    setCollapsedWeeks(prev => ({
      ...prev,
      [newId]: true
    }))
  }

  const removeMonthlyEntry = (id) => {
    if (monthlyEntries.length > 1) {
      setMonthlyEntries(prev => prev.filter(entry => entry.id !== id))
    }
  }

  const toggleWeekCollapse = (weekId) => {
    setCollapsedWeeks(prev => ({
      ...prev,
      [weekId]: !prev[weekId]
    }))
  }

  const addWorkDay = (weekId) => {
    setMonthlyEntries(prev => prev.map(week => {
      if (week.id === weekId) {
        const newDayId = Math.max(...week.work_days.map(day => day.day_id)) + 1
        
        // Calculate the next work date by incrementing the latest existing date
        let newWorkDate = new Date().toISOString().split('T')[0]
        if (week.work_days.length > 0) {
          const latestDate = new Date(Math.max(...week.work_days.map(day => new Date(day.work_date))))
          latestDate.setDate(latestDate.getDate() + 1)
          newWorkDate = latestDate.toISOString().split('T')[0]
        }
        
        console.log(`Adding new day ${newDayId} with work date: ${newWorkDate}`)
        
                  return {
            ...week,
            work_days: [...week.work_days, {
              day_id: newDayId,
              work_date: newWorkDate,
              from_hours: '',
              to_hours: '',
              total_hours: '',
              day_amount: ''
            }]
          }
      }
      return week
    }))
  }

  const removeWorkDay = (weekId, dayId) => {
    setMonthlyEntries(prev => prev.map(week => {
      if (week.id === weekId && week.work_days.length > 1) {
        return {
          ...week,
          work_days: week.work_days.filter(day => day.day_id !== dayId)
        }
      }
      return week
    }))
  }

  const updateWeeklyField = (weekId, field, value) => {
    setMonthlyEntries(prev => prev.map(week => {
      if (week.id === weekId) {
        return { ...week, [field]: value }
      }
      return week
    }))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Debug Information - Hidden by default */}
      {false && editingDriver && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 className="text-sm font-medium text-yellow-800 mb-2">Debug Info:</h3>
          <div className="text-xs text-yellow-700">
            <div>Driver ID: {editingDriver.id}</div>
            <div>Name: {editingDriver.name}</div>
            <div>Phone: {editingDriver.phone_number}</div>
            <div>Has weeks_data: {editingDriver.weeks_data ? 'Yes' : 'No'}</div>
            {editingDriver.weeks_data && (
              <div>Weeks count: {editingDriver.weeks_data.length}</div>
            )}
            <div>Current monthlyEntries: {monthlyEntries.length}</div>
            <div>Form Data: {JSON.stringify(formData, null, 2)}</div>
          </div>
        </div>
      )}
      
      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <div className="text-green-600 mr-2">✓</div>
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        </div>
      )}
      

      
      {/* Driver Basic Information Section */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <User className="h-5 w-5 mr-2 text-brand-red" />
          Driver Basic Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Driver Name */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Driver Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`input ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="Enter driver's full name"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Phone Number */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number *
            </label>
                                     <input
              type="tel"
              value={formData.phone_number}
              onChange={(e) => handleInputChange('phone_number', formatPhoneNumber(e.target.value))}
              className={`input ${errors.phone_number ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="1234567890"
              maxLength="10"
            />
            {errors.phone_number && <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>}
          </div>



          

           {/* Advance Amount */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">
               Advance Amount (₹)
             </label>
             <input
               type="number"
               value={formData.advance_amount}
               onChange={(e) => handleInputChange('advance_amount', e.target.value)}
               className={`input ${errors.advance_amount ? 'border-red-500 focus:ring-red-500' : ''}`}
               placeholder="0.00"
               min="0"
               step="0.01"
             />
             {errors.advance_amount && <p className="text-red-500 text-xs mt-1">{errors.advance_amount}</p>}
             <p className="text-gray-600 text-xs mt-1">
               Monthly advance payment given to driver
             </p>
             
             {/* Remaining Advance Amount */}
             <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
               <p className="text-sm text-yellow-800">
                 <strong>Remaining Advance:</strong> ₹{Math.max(0, (parseFloat(formData.advance_amount) || 0) - monthlyEntries.reduce((total, week) => total + (parseFloat(week.advance_deduction) || 0), 0)).toFixed(2)}
               </p>
             </div>
           </div>

        </div>
      </div>

      {/* Weekly Work Details Section */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Clock className="h-5 w-5 mr-2 text-blue-600" />
            Weekly Work Details
          </h3>
          <button
            type="button"
            onClick={addMonthlyEntry}
            className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 text-sm font-medium shadow-lg"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Another Week
          </button>
        </div>
        
        {monthlyEntries.map((week, weekIndex) => (
          <div key={week.id} className="bg-white p-4 rounded-lg border border-blue-200 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => toggleWeekCollapse(week.id)}
                  className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors duration-200"
                  title={collapsedWeeks[week.id] ? "Expand week" : "Collapse week"}
                >
                  {collapsedWeeks[week.id] ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  )}
                </button>
                <h4 className="text-md font-medium text-gray-900">
                  Week {weekIndex + 1}
                </h4>
              </div>
              <div className="flex items-center space-x-2">
                {!collapsedWeeks[week.id] && (
                  <button
                    type="button"
                    onClick={() => addWorkDay(week.id)}
                    className="inline-flex items-center px-2 py-1 bg-gradient-to-r from-brand-orange to-brand-red text-white rounded text-xs hover:from-orange-500 hover:to-red-600 transition-all duration-200 shadow-sm"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Day
                  </button>
                )}
                {monthlyEntries.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeMonthlyEntry(week.id)}
                    className="text-red-600 hover:text-red-800 transition-colors duration-200"
                    title="Remove this week"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Collapsible Week Content */}
            {!collapsedWeeks[week.id] ? (
              <>
                {/* Weekly Salary and Advance Deduction */}
                <div className="mb-4 space-y-4">

              {/* Advance Deduction */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Advance Deduction (₹) - Optional
                </label>
                <input
                  type="number"
                  value={week.advance_deduction}
                  onChange={(e) => updateWeeklyField(week.id, 'advance_deduction', e.target.value)}
                  className="input"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
                <p className="text-gray-600 text-xs mt-1">
                  Enter amount to deduct from advance
                </p>
              </div>
            </div>

            {/* Work Days */}
            <div className="space-y-4">
              <h5 className="text-sm font-medium text-gray-700">Work Days:</h5>
              {week.work_days.map((day, dayIndex) => (
                <div key={day.day_id} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h6 className="text-sm font-medium text-gray-600">
                      Day {dayIndex + 1}
                    </h6>
                    {week.work_days.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeWorkDay(week.id, day.day_id)}
                        className="text-red-500 hover:text-red-700 transition-colors duration-200"
                        title="Remove this day"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Work Date */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Work Date *
                      </label>
                      <input
                        type="date"
                        value={day.work_date}
                        onChange={(e) => updateWorkDay(week.id, day.day_id, 'work_date', e.target.value)}
                        className={`input text-sm ${monthlyErrors[week.id]?.[day.day_id]?.work_date ? 'border-red-500 focus:ring-red-500' : ''}`}
                      />
                      {monthlyErrors[week.id]?.[day.day_id]?.work_date && <p className="text-red-500 text-xs mt-1">{monthlyErrors[week.id][day.day_id].work_date}</p>}
                    </div>

                                         
                     {/* In Time */}
                     <div>
                       <label className="block text-xs font-medium text-gray-600 mb-1">
                         In Time
                       </label>
                       <input
                         type="time"
                         value={day.from_hours || ''}
                         onChange={(e) => updateWorkDay(week.id, day.day_id, 'from_hours', e.target.value)}
                         className="input text-sm"
                         placeholder="09:00"
                       />
                       <p className="text-gray-500 text-xs mt-1">
                         Driver check-in time
                       </p>
                     </div>

                     {/* Out Time */}
                     <div>
                       <label className="block text-xs font-medium text-gray-600 mb-1">
                         Out Time
                       </label>
                       <input
                         type="time"
                         value={day.to_hours || ''}
                         onChange={(e) => updateWorkDay(week.id, day.day_id, 'to_hours', e.target.value)}
                         className="input text-sm"
                         placeholder="17:00"
                       />
                       <p className="text-gray-500 text-xs mt-1">
                         Driver check-out time
                       </p>
                     </div>

                     {/* Total Hours (Manual Entry) */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Total Hours *
                      </label>
                      <input
                        type="number"
                        value={day.total_hours}
                        onChange={(e) => updateWorkDay(week.id, day.day_id, 'total_hours', parseFloat(e.target.value) || 0)}
                        className={`input text-sm ${monthlyErrors[week.id]?.[day.day_id]?.total_hours ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                      {monthlyErrors[week.id]?.[day.day_id]?.total_hours && <p className="text-red-500 text-xs mt-1">{monthlyErrors[week.id][day.day_id].total_hours}</p>}
                      <p className="text-gray-500 text-xs mt-1">
                        Enter total hours worked manually
                      </p>
                    </div>

                    {/* Day Amount */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Day Amount (₹)
                      </label>
                      <input
                        type="number"
                        value={day.day_amount || ''}
                        onChange={(e) => updateWorkDay(week.id, day.day_id, 'day_amount', e.target.value)}
                        className="input text-sm"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                      <p className="text-gray-500 text-xs mt-1">
                        Amount for this day
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Weekly Total and Final Amount */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Weekly Total Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weekly Total Hours
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={`${week.work_days.reduce((total, day) => total + day.total_hours, 0)} hours`}
                      readOnly
                      className="input bg-blue-50 text-blue-700 font-medium"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-blue-600">
                      Total
                    </div>
                  </div>
                </div>


                {/* Final Amount After Deduction */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Final Salary (₹)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={`₹${Math.max(0, (week.work_days.reduce((total, day) => total + (parseFloat(day.day_amount) || 0), 0)) - (parseFloat(week.advance_deduction) || 0)).toFixed(2)}`}
                      readOnly
                      className="input bg-green-50 text-green-700 font-medium"
                    />
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-green-600">
                      Final
                    </div>
                  </div>
                  <p className="text-gray-600 text-xs mt-1">
                    Total Day Amounts - Advance Deduction
                  </p>
                </div>
              </div>
            </div>
              </>
            ) : (
              /* Collapsed Week Summary */
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm text-gray-600">
                  <div className="flex justify-between items-center">
                    <span>Work Days: {week.work_days.length}</span>
                    <span>Total Hours: {week.work_days.reduce((total, day) => total + (day.total_hours || 0), 0)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-3 bg-gradient-to-r from-brand-orange to-brand-red text-white rounded-lg hover:from-orange-500 hover:to-red-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium shadow-lg"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              {editingDriver ? 'Update Driver' : 'Add Driver'}
            </>
          )}
        </button>
      </div>
    </form>
  )
}

export default DriverForm
